import type { ReactNode } from "react";
import type { AppMetricsResponse, UsageResponse } from "@apphq/shared/types";
import { getAppMetrics, getUsage } from "../lib/api-admin";
import { PERIOD_LONG, countryNl, eur, int, pct } from "../lib/format";
import { usePrefs } from "../lib/prefs";
import { useAsync } from "../lib/useAsync";
import { RETENTION_DAYS, cohortLabel, cohortValue, cohortsNewestFirst, funnelIsEmpty, funnelSteps, retentionCellStyle, retentionIsEmpty, sessionsPerUser } from "../lib/usage";
import { UsageChart } from "./LazyCharts";
import { BarList, Card, EmptyState, ErrorState } from "./ui";
import { SkeletonRows } from "./skeletons";

/** Usage ignores the environment (events carry none), so the env toggle never refetches; the period does. */
export function useUsage(appId: string | null) {
  const { period } = usePrefs();
  return useAsync(() => getUsage({ app_id: appId || undefined, period, environment: "production" }), [appId, period]);
}
export type UsageState = ReturnType<typeof useUsage>;

/** Shown on every usage card: usage aggregates count production (App Store) installs only; sandbox/TestFlight is excluded (recordEvents gate). */
export const APP_STORE_ONLY_NOTE = "alleen App Store";
function EnvBadge() {
  return <span className="muted small">{APP_STORE_ONLY_NOTE}</span>;
}

function Body({ state, title, aside, children }: { state: UsageState; title: string; aside?: ReactNode; children: (d: UsageResponse) => ReactNode }) {
  const d = state.data;
  return (
    <Card title={title} aside={aside ?? <EnvBadge />}>
      {state.status === "error" && <ErrorState message={state.error} onRetry={state.reload} />}
      {state.status === "loading" && <SkeletonRows rows={4} icon={false} />}
      {state.status === "ready" && d && children(d)}
    </Card>
  );
}

const EMPTY_TEXT = "Zodra een app met AppHQKit events stuurt, verschijnen hier de cijfers.";

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="device-tile">
      <div className="kpi-label">{label}</div>
      <div className="device-tile-value">{value}</div>
      {hint && <div className="muted small">{hint}</div>}
    </div>
  );
}

/** DAU / MAU / stickiness + DAU line. */
export function UsageKpiCard({ state }: { state: UsageState }) {
  const { period } = usePrefs();
  return (
    <Body state={state} title="Gebruik">
      {(d) =>
        d.mau === 0 && d.dau === 0 && d.series.every((p) => p.dau === 0) ? (
          <EmptyState title="Nog geen gebruik" icon="∿">{EMPTY_TEXT}</EmptyState>
        ) : (
          <>
            <div className="device-hero">
              <div>
                <div className="big-number">{int(d.dau)}</div>
                <div className="muted">actieve gebruikers vandaag (DAU)</div>
              </div>
              <div className="device-split">
                <Tile label="MAU" value={int(d.mau)} hint="afgelopen 30 dagen" />
                <Tile label="DAU/MAU" value={pct(d.stickiness)} hint="stickiness" />
                <Tile label="Nieuwe gebruikers" value={int(d.new_users)} hint={PERIOD_LONG[period]} />
                <Tile label="Sessies/gebruiker" value={perUser(sessionsPerUser(d.sessions, d.mau))} hint={`${int(d.sessions)} sessies`} />
                <Tile label="Events/sessie" value={perUser(d.events_per_session ?? null)} hint="intensiteit, geen minuten; echte sessieduur volgt met SDK v0.1.5" />
              </div>
            </div>
            {d.series.length >= 2 ? (
              <UsageChart points={d.series} />
            ) : (
              <p className="muted tight">De trendlijn bouwt zich op met de dagelijkse rollup; vanaf de tweede dag verschijnt de grafiek.</p>
            )}
          </>
        )
      }
    </Body>
  );
}

function perUser(n: number | null): string {
  return n === null ? "—" : n.toLocaleString("nl-NL", { maximumFractionDigits: 1 });
}
/** Cohort grid: rows per week, columns day 1/7/14/30, blue by percentage, immature cells neutral. */
export function RetentionCard({ state }: { state: UsageState }) {
  return (
    <Body state={state} title="Retentie">
      {(d) => {
        const rows = cohortsNewestFirst(d.retention.cohorts);
        if (rows.length === 0 || retentionIsEmpty(rows)) return <EmptyState title="Nog geen cohorten" icon="▥">Retentiecohorten verschijnen zodra gebruikers events sturen.</EmptyState>;
        return (
          <>
            <div className="retention-scroll">
              <table className="retention">
                <thead>
                  <tr>
                    <th scope="col">Cohort</th>
                    <th scope="col" className="num">Gebruikers</th>
                    {RETENTION_DAYS.map((n) => (
                      <th key={n} scope="col" className="num">Dag {n}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.cohort_start}>
                      <th scope="row">{cohortLabel(r.cohort_start)}</th>
                      <td className="num">{int(r.users)}</td>
                      {RETENTION_DAYS.map((n) => {
                        const c = retentionCellStyle(cohortValue(r, n));
                        return (
                          <td key={n} className="num">
                            <span
                              className={c.tint === null ? "ret-cell ret-empty" : c.light ? "ret-cell ret-light" : "ret-cell"}
                              style={c.tint === null ? undefined : { background: `color-mix(in srgb, var(--accent) ${c.tint}%, var(--card))` }}
                              title={c.tint === null ? "Nog niet rijp: de dag is nog niet voorbij" : undefined}
                            >
                              {c.text}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted small tight">Cohort = eerste dag met een event. Een lege cel (–) is nog niet rijp, geen 0%.</p>
          </>
        );
      }}
    </Body>
  );
}

/** Bekeken -> gestart -> betaald. */
export function FunnelCard({ state }: { state: UsageState }) {
  return (
    <Body state={state} title="Paywall-funnel">
      {(d) => {
        if (funnelIsEmpty(d.funnel)) return <EmptyState title="Nog geen paywall-events" icon="⌄">Bekeken → gestart → betaald komt uit de SDK-events.</EmptyState>;
        return <FunnelBars funnel={d.funnel} />;
      }}
    </Body>
  );
}

export function FunnelBars({ funnel }: { funnel: UsageResponse["funnel"] }) {
  const steps = funnelSteps(funnel);
  return (
    <>
      <ul className="funnel">
        {steps.map((s) => (
          <li key={s.key}>
            <div className="bar-row">
              <span className="bar-label">
                {s.label}
                {s.rate !== null && <span className="muted small"> · {pct(s.rate)} van vorige stap</span>}
              </span>
              <span className="bar-value">{int(s.value)}</span>
            </div>
            <div className="bar-track">
              <div className="bar-fill" style={{ width: `${s.widthPercent}%` }} />
            </div>
          </li>
        ))}
      </ul>
      <p className="tight">
        Conversie bekeken → betaald: <strong>{pct(funnel.conversion)}</strong>
        {funnel.cancelled > 0 && <span className="muted"> · {int(funnel.cancelled)} geannuleerd</span>}
      </p>
    </>
  );
}

export function TopEventsCard({ state }: { state: UsageState }) {
  return (
    <Body state={state} title="Top-events">
      {(d) =>
        d.top_events.length === 0 ? (
          <EmptyState title="Nog geen events" icon="★">De meest voorkomende events uit de SDK.</EmptyState>
        ) : (
          <BarList rows={d.top_events.map((e) => ({ key: e.event, label: <span className="mono">{e.event}</span>, value: e.count }))} format={int} />
        )
      }
    </Body>
  );
}

/** Product mix + top countries come from the per-app revenue metrics (so they follow env/VAT/period prefs). */
export function useAppMetricsFor(appId: string | null) {
  const { env, period, vat } = usePrefs();
  return useAsync(
    () => (appId ? getAppMetrics(appId, { period, environment: env, vat }) : Promise.resolve(null as AppMetricsResponse | null)),
    [appId, period, env, vat],
  );
}

export function MixAndCountries({ appId }: { appId: string }) {
  const state = useAppMetricsFor(appId);
  const m = state.data;
  const wait = (title: string, body: (m: AppMetricsResponse) => ReactNode, aside?: ReactNode) => (
    <Card title={title} aside={aside}>
      {state.status === "error" && <ErrorState message={state.error} onRetry={state.reload} />}
      {state.status === "loading" && <SkeletonRows rows={4} icon={false} />}
      {state.status === "ready" && m && body(m)}
    </Card>
  );
  return (
    <div className="grid-2">
      {wait("Productmix", (x) =>
        x.product_mix.length === 0 || x.product_mix.every((p) => p.revenue === 0 && p.active === 0) ? (
          <EmptyState title="Nog geen omzet per product" />
        ) : (
          <BarList rows={x.product_mix.map((p) => ({ key: p.product_id, label: <span className="mono">{p.product_id}</span>, value: p.revenue }))} format={eur} />
        ),
      )}
      {wait(
        "Top-landen",
        (x) =>
          x.top_countries.length === 0 ? (
            <EmptyState title="Nog geen omzet per land" />
          ) : (
            <BarList rows={x.top_countries.map((c) => ({ key: c.country, label: `${countryNl(c.country)} · ${int(c.transactions)}×`, value: c.revenue }))} format={eur} />
          ),
      )}
    </div>
  );
}
